import * as nls from '@vscode-alt/monaco-editor/esm/vs/nls';
import { Emitter, Event } from '@vscode-alt/monaco-editor/esm/vs/base/common/event';
import { Disposable, IDisposable } from '@vscode-alt/monaco-editor/esm/vs/base/common/lifecycle';
import { OS, OperatingSystem, isMacintosh, isWindows } from '@vscode-alt/monaco-editor/esm/vs/base/common/platform';
import { IKeyboardEvent } from '@vscode-alt/monaco-editor/esm/vs/platform/keybinding/common/keybinding';
import { StandardKeyboardEvent } from '@vscode-alt/monaco-editor/esm/vs/base/browser/keyboardEvent';
import { URI } from '@vscode-alt/monaco-editor/esm/vs/base/common/uri';
import { RunOnceScheduler } from '@vscode-alt/monaco-editor/esm/vs/base/common/async';
import { parse } from '@vscode-alt/monaco-editor/esm/vs/base/common/json';
import * as objects from '@vscode-alt/monaco-editor/esm/vs/base/common/objects';
import { injectable, inject, decorate, postConstruct } from 'inversify';
import {
    IKeyboardMapper, IKeymapService, IKeyboardLayoutInfo, IKeyboardMapping, IWindowsKeyboardMapping,
    DispatchConfig, ClientTypes, IClientConfigurationService, INotificationService, ICommandService,
    IStorageService, IEnvironmentService, LifecyclePhase, ILifecycleService,
} from '@workbench-stack/core';
import { IClientFileService as IFileService, ClientTypes as FilesTypes } from '@files-stack/core';
import {
    IMacLinuxKeyboardMapping, MacLinuxKeyboardMapper, MacLinuxFallbackKeyboardMapper,
    WindowsKeyboardMapper, CachedKeyboardMapper, KeymapInfo, IRawMixedKeyboardMapping, getKeyboardLayoutId,
    IKeymapInfo, getDispatchConfig,
} from './keyboardMapper';

export interface IKeyboard {
    getLayoutMap(): Promise<Object>;
    lock(keyCodes?: string[]): Promise<void>;
    unlock(): void;
    addEventListener?(type: string, listener: () => void): void;
}
export type INavigatorWithKeyboard = Navigator & {
    keyboard: IKeyboard,
};
export type NodeType = 'object' | 'array' | 'property' | 'string' | 'number' | 'boolean' | 'null';
export function getNodeType(value: any): NodeType {
    switch (typeof value) {
        case 'boolean': return 'boolean';
        case 'number': return 'number';
        case 'string': return 'string';
        case 'object': {
            if (!value) {
                return 'null';
            } else if (Array.isArray(value)) {
                return 'array';
            }
            return 'object';
        }
        default: return 'null';
    }
}

export class BrowserKeyboardMapperFactoryBase {
    // keyboard mapper
    protected _initialized: boolean;
    protected _keyboardMapper: IKeyboardMapper | null;
    private readonly _onDidChangeKeyboardMapper = new Emitter<void>();
    public readonly onDidChangeKeyboardMapper: Event<void> = this._onDidChangeKeyboardMapper.event;

    // keymap infos
    protected _keymapInfos: KeymapInfo[];
    protected _mru: KeymapInfo[];
    private _activeKeymapInfo: KeymapInfo | null;

    get activeKeymap(): KeymapInfo | null {
        return this._activeKeymapInfo;
    }

    get keymapInfos(): KeymapInfo[] {
        return this._keymapInfos;
    }

    get activeKeyboardLayout(): IKeyboardLayoutInfo | null {
        if (!this._initialized) {
            return null;
        }

        return this._activeKeymapInfo && this._activeKeymapInfo.layout;
    }

    get activeKeyMapping(): IKeyboardMapping | null {
        if (!this._initialized) {
            return null;
        }

        return this._activeKeymapInfo && this._activeKeymapInfo.mapping;
    }

    get keyboardLayouts(): IKeyboardLayoutInfo[] {
        return this._keymapInfos.map(keymapInfo => keymapInfo.layout);
    }

    protected constructor(
        // private _notificationService: INotificationService,
        // private _storageService: IStorageService,
        // private _commandService: ICommandService
    ) {
        this._keyboardMapper = null;
        this._initialized = false;
        this._keymapInfos = [];
        this._mru = [];
        this._activeKeymapInfo = null;

        if ((<INavigatorWithKeyboard>navigator).keyboard && (<INavigatorWithKeyboard>navigator).keyboard.addEventListener) {
            (<INavigatorWithKeyboard>navigator).keyboard.addEventListener!('layoutchange', () => {
                // Update user keyboard map settings
                this._getBrowserKeyMapping().then((mapping: IKeyboardMapping | null) => {
                    if (this.isKeyMappingActive(mapping)) {
                        return;
                    }

                    this.onKeyboardLayoutChanged();
                });
            });
        }
        this.onKeyboardLayoutChanged();
    }

    public registerKeyboardLayout(layout: KeymapInfo) {
        this._keymapInfos.push(layout);
        this._mru = this._keymapInfos;
    }

    public removeKeyboardLayout(layout: KeymapInfo): void {
        let index = this._mru.indexOf(layout);
        this._mru.splice(index, 1);
        index = this._keymapInfos.indexOf(layout);
        this._keymapInfos.splice(index, 1);
    }

    public getMatchedKeymapInfo(keyMapping: IKeyboardMapping | null): { result: KeymapInfo, score: number } | null {
        if (!keyMapping) {
            return null;
        }

        let usStandard = this.getUSStandardLayout();

        if (usStandard) {
            let maxScore = usStandard.getScore(keyMapping);
            if (maxScore === 0) {
                return {
                    result: usStandard,
                    score: 0,
                };
            }

            let result = usStandard;
            for (let i = 0; i < this._mru.length; i++) {
                let score = this._mru[i].getScore(keyMapping);
                if (score > maxScore) {
                    if (score === 0) {
                        return {
                            result: this._mru[i],
                            score: 0,
                        };
                    }

                    maxScore = score;
                    result = this._mru[i];
                }
            }

            return {
                result,
                score: maxScore,
            };
        }

        for (let i = 0; i < this._mru.length; i++) {
            if (this._mru[i].fuzzyEqual(keyMapping)) {
                return {
                    result: this._mru[i],
                    score: 0,
                };
            }
        }

        return null;
    }

    public getUSStandardLayout() {
        const usStandardLayouts = this._mru.filter(layout => layout.layout.isUSStandard);

        if (usStandardLayouts.length) {
            return usStandardLayouts[0];
        }

        return null;
    }

    public isKeyMappingActive(keymap: IKeyboardMapping | null) {
        return this._activeKeymapInfo && keymap && this._activeKeymapInfo.fuzzyEqual(keymap);
    }

    public setUSKeyboardLayout() {
        this._activeKeymapInfo = this.getUSStandardLayout();
    }

    public setActiveKeyMapping(keymap: IKeyboardMapping | null) {
        let keymapUpdated = false;
        let matchedKeyboardLayout = this.getMatchedKeymapInfo(keymap);
        if (matchedKeyboardLayout) {
            // let score = matchedKeyboardLayout.score;

            // Due to https://bugs.chromium.org/p/chromium/issues/detail?id=977609, any key after a dead key will generate a wrong mapping,
            // we shoud avoid yielding the false error.
            // if (keymap && score < 0) {
            // const donotAskUpdateKey = 'missing.keyboardlayout.donotask';
            // if (this._storageService.getBoolean(donotAskUpdateKey, StorageScope.GLOBAL)) {
            // 	return;
            // }

            // // the keyboard layout doesn't actually match the key event or the keymap from chromium
            // this._notificationService.prompt(
            // 	Severity.Info,
            // 	nls.localize('missing.keyboardlayout', 'Fail to find matching keyboard layout'),
            // 	[{
            // 		label: nls.localize('keyboardLayoutMissing.configure', "Configure"),
            // 		run: () => this._commandService.executeCommand('workbench.action.openKeyboardLayoutPicker')
            // 	}, {
            // 		label: nls.localize('neverAgain', "Don't Show Again"),
            // 		isSecondary: true,
            // 		run: () => this._storageService.store(donotAskUpdateKey, true, StorageScope.GLOBAL)
            // 	}]
            // );

            // console.warn('Active keymap/keyevent does not match current keyboard layout', JSON.stringify(keymap), this._activeKeymapInfo ? JSON.stringify(this._activeKeymapInfo.layout) : '');

            // return;
            // }

            if (!this._activeKeymapInfo) {
                this._activeKeymapInfo = matchedKeyboardLayout.result;
                keymapUpdated = true;
            } else if (keymap) {
                if (matchedKeyboardLayout.result.getScore(keymap) > this._activeKeymapInfo.getScore(keymap)) {
                    this._activeKeymapInfo = matchedKeyboardLayout.result;
                    keymapUpdated = true;
                }
            }
        }

        if (!this._activeKeymapInfo) {
            this._activeKeymapInfo = this.getUSStandardLayout();
            keymapUpdated = true;
        }

        if (!this._activeKeymapInfo || !keymapUpdated) {
            return;
        }

        const index = this._mru.indexOf(this._activeKeymapInfo);

        this._mru.splice(index, 1);
        this._mru.unshift(this._activeKeymapInfo);

        this._setKeyboardData(this._activeKeymapInfo);
    }

    public setActiveKeymapInfo(keymapInfo: KeymapInfo) {
        this._activeKeymapInfo = keymapInfo;

        const index = this._mru.indexOf(this._activeKeymapInfo);

        if (index === 0) {
            return;
        }

        this._mru.splice(index, 1);
        this._mru.unshift(this._activeKeymapInfo);

        this._setKeyboardData(this._activeKeymapInfo);
    }

    public onKeyboardLayoutChanged(): void {
        this._updateKeyboardLayoutAsync(this._initialized);
    }

    private _updateKeyboardLayoutAsync(initialized: boolean, keyboardEvent?: IKeyboardEvent) {
        if (!initialized) {
            return;
        }

        this._getBrowserKeyMapping(keyboardEvent).then(keyMap => {
            // might be false positive
            if (this.isKeyMappingActive(keyMap)) {
                return;
            }
            this.setActiveKeyMapping(keyMap);
        });
    }

    public getKeyboardMapper(dispatchConfig: DispatchConfig): IKeyboardMapper {
        if (!this._initialized) {
            return new MacLinuxFallbackKeyboardMapper(OS);
        }
        if (dispatchConfig === DispatchConfig.KeyCode) {
            // Forcefully set to use keyCode
            return new MacLinuxFallbackKeyboardMapper(OS);
        }
        return this._keyboardMapper!;
    }

    public validateCurrentKeyboardMapping(keyboardEvent: IKeyboardEvent): void {
        if (!this._initialized) {
            return;
        }

        let isCurrentKeyboard = this._validateCurrentKeyboardMapping(keyboardEvent);

        if (isCurrentKeyboard) {
            return;
        }

        this._updateKeyboardLayoutAsync(true, keyboardEvent);
    }

    public setKeyboardLayout(layoutName: string) {
        let matchedLayouts: KeymapInfo[] = this.keymapInfos.filter(keymapInfo => getKeyboardLayoutId(keymapInfo.layout) === layoutName);

        if (matchedLayouts.length > 0) {
            this.setActiveKeymapInfo(matchedLayouts[0]);
        }
    }

    private _setKeyboardData(keymapInfo: KeymapInfo): void {
        this._initialized = true;

        this._keyboardMapper = new CachedKeyboardMapper(BrowserKeyboardMapperFactory._createKeyboardMapper(keymapInfo));
        this._onDidChangeKeyboardMapper.fire();
    }

    private static _createKeyboardMapper(keymapInfo: KeymapInfo): IKeyboardMapper {
        let rawMapping = keymapInfo.mapping;
        const isUSStandard = !!keymapInfo.layout.isUSStandard;
        if (OS === OperatingSystem.Windows) {
            return new WindowsKeyboardMapper(isUSStandard, <IWindowsKeyboardMapping>rawMapping);
        }
        if (Object.keys(rawMapping).length === 0) {
            // Looks like reading the mappings failed (most likely Mac + Japanese/Chinese keyboard layouts)
            return new MacLinuxFallbackKeyboardMapper(OS);
        }

        return new MacLinuxKeyboardMapper(isUSStandard, <IMacLinuxKeyboardMapping>rawMapping, OS);
    }

    //#region Browser API
    private _validateCurrentKeyboardMapping(keyboardEvent: IKeyboardEvent): boolean {
        if (!this._initialized) {
            return true;
        }

        const standardKeyboardEvent = keyboardEvent as StandardKeyboardEvent;
        const currentKeymap = this._activeKeymapInfo;
        if (!currentKeymap) {
            return true;
        }

        if (standardKeyboardEvent.browserEvent.key === 'Dead' || standardKeyboardEvent.browserEvent.isComposing) {
            return true;
        }

        const mapping = currentKeymap.mapping[standardKeyboardEvent.code];

        if (!mapping) {
            return false;
        }

        if (mapping.value === '') {
            // The value is empty when the key is not a printable character, we skip validation.
            if (keyboardEvent.ctrlKey || keyboardEvent.metaKey) {
                setTimeout(() => {
                    this._getBrowserKeyMapping().then((keymap: IRawMixedKeyboardMapping | null) => {
                        if (this.isKeyMappingActive(keymap)) {
                            return;
                        }

                        this.onKeyboardLayoutChanged();
                    });
                }, 350);
            }
            return true;
        }

        const expectedValue = standardKeyboardEvent.altKey && standardKeyboardEvent.shiftKey ? mapping.withShiftAltGr :
            standardKeyboardEvent.altKey ? mapping.withAltGr :
                standardKeyboardEvent.shiftKey ? mapping.withShift : mapping.value;

        const isDead = (standardKeyboardEvent.altKey && standardKeyboardEvent.shiftKey && mapping.withShiftAltGrIsDeadKey) ||
            (standardKeyboardEvent.altKey && mapping.withAltGrIsDeadKey) ||
            (standardKeyboardEvent.shiftKey && mapping.withShiftIsDeadKey) ||
            mapping.valueIsDeadKey;

        if (isDead && standardKeyboardEvent.browserEvent.key !== 'Dead') {
            return false;
        }

        // TODO, this assumption is wrong as `browserEvent.key` doesn't necessarily equal expectedValue from real keymap
        if (!isDead && standardKeyboardEvent.browserEvent.key !== expectedValue) {
            return false;
        }

        return true;
    }

    private async _getBrowserKeyMapping(keyboardEvent?: IKeyboardEvent): Promise<IRawMixedKeyboardMapping | null> {
        if ((navigator as any).keyboard) {
            try {
                return (navigator as any).keyboard.getLayoutMap().then((e: any) => {
                    let ret: IKeyboardMapping = {};
                    for (let key of e) {
                        ret[key[0]] = {
                            'value': key[1],
                            'withShift': '',
                            'withAltGr': '',
                            'withShiftAltGr': '',
                        };
                    }

                    return ret;

                    // const matchedKeyboardLayout = this.getMatchedKeymapInfo(ret);

                    // if (matchedKeyboardLayout) {
                    // 	return matchedKeyboardLayout.result.mapping;
                    // }

                    // return null;
                });
            } catch {
                // getLayoutMap can throw if invoked from a nested browsing context
            }
        } else if (keyboardEvent && !keyboardEvent.shiftKey && !keyboardEvent.altKey && !keyboardEvent.metaKey && !keyboardEvent.metaKey) {
            let ret: IKeyboardMapping = {};
            const standardKeyboardEvent = keyboardEvent as StandardKeyboardEvent;
            ret[standardKeyboardEvent.browserEvent.code] = {
                'value': standardKeyboardEvent.browserEvent.key,
                'withShift': '',
                'withAltGr': '',
                'withShiftAltGr': '',
            };

            const matchedKeyboardLayout = this.getMatchedKeymapInfo(ret);

            if (matchedKeyboardLayout) {
                return ret;
            }

            return null;
        }

        return null;
    }

    //#endregion
}
const TRACK_DISPOSABLES = false;

const __is_disposable_tracked__ = '__is_disposable_tracked__';
function markTracked<T extends IDisposable>(x: T): void {
    if (!TRACK_DISPOSABLES) {
        return;
    }

    if (x && x !== Disposable.None) {
        try {
            (x as any)[__is_disposable_tracked__] = true;
        } catch {
            // noop
        }
    }
}
function trackDisposable<T extends IDisposable>(x: T): T {
    if (!TRACK_DISPOSABLES) {
        return x;
    }

    const stack = new Error('Potentially leaked disposable').stack!;
    setTimeout(() => {
        if (!(x as any)[__is_disposable_tracked__]) {
            console.log(stack);
        }
    }, 3000);
    return x;
}
export class MutableDisposable<T extends IDisposable> implements IDisposable {
    private _value?: T;
    private _isDisposed = false;

    constructor() {
        trackDisposable(this);
    }

    get value(): T | undefined {
        return this._isDisposed ? undefined : this._value;
    }

    set value(value: T | undefined) {
        if (this._isDisposed || value === this._value) {
            return;
        }

        if (this._value) {
            this._value.dispose();
        }
        if (value) {
            markTracked(value);
        }
        this._value = value;
    }

    public clear() {
        this.value = undefined;
    }

    public dispose(): void {
        this._isDisposed = true;
        markTracked(this);
        if (this._value) {
            this._value.dispose();
        }
        this._value = undefined;
    }
}

export class BrowserKeyboardMapperFactory extends BrowserKeyboardMapperFactoryBase {
    constructor(notificationService: INotificationService, storageService: IStorageService, commandService: ICommandService) {
        // super(notificationService, storageService, commandService);
        super();

        const platform = isWindows ? 'win' : isMacintosh ? 'darwin' : 'linux';

        import('./keyboardLayouts/layout.contribution.' + platform).then((m) => {
            let keymapInfos: IKeymapInfo[] = m.KeyboardLayoutContribution.INSTANCE.layoutInfos;
            this._keymapInfos.push(...keymapInfos.map(info => (new KeymapInfo(info.layout, info.secondaryLayouts, info.mapping, info.isUserKeyboardLayout))));
            this._mru = this._keymapInfos;
            this._initialized = true;
            this.onKeyboardLayoutChanged();
        });
    }
}

class UserKeyboardLayout extends Disposable {

    private readonly reloadConfigurationScheduler: RunOnceScheduler;
    protected readonly _onDidChange: Emitter<void> = this._register(new Emitter<void>());
    public readonly onDidChange: Event<void> = this._onDidChange.event;

    private _keyboardLayout: KeymapInfo | null;
    get keyboardLayout(): KeymapInfo | null { return this._keyboardLayout; }

    constructor(
        private readonly keyboardLayoutResource: URI,
        private readonly fileService: IFileService,
    ) {
        super();

        this._keyboardLayout = null;

        this.reloadConfigurationScheduler = this._register(new RunOnceScheduler(() => this.reload().then(changed => {
            if (changed) {
                this._onDidChange.fire();
            }
        }), 50));

        // this._register(Event.filter(this.fileService.onDidFilesChange, e => e.contains(this.keyboardLayoutResource))(() => this.reloadConfigurationScheduler.schedule()));
    }

    public async initialize(): Promise<void> {
        await this.reload();
    }

    private async reload(): Promise<boolean> {
        const existing = this._keyboardLayout;
        try {
            // const content = await this.fileService.readFile(this.keyboardLayoutResource);
            // const value = parse(content.value.toString());
            // if (getNodeType(value) === 'object') {
            //     const layoutInfo = value.layout;
            //     const mappings = value.rawMapping;
            //     this._keyboardLayout = KeymapInfo.createKeyboardLayoutFromDebugInfo(layoutInfo, mappings, true);
            // } else {
            //     this._keyboardLayout = null;
            // }
        } catch (e) {
            this._keyboardLayout = null;
        }

        return existing ? !objects.equals(existing, this._keyboardLayout) : true;
    }

}

decorate(injectable(), Disposable);

@injectable()
export class BrowserKeymapService extends Disposable implements IKeymapService {
    public _serviceBrand: undefined;

    private readonly _onDidChangeKeyboardMapper = new Emitter<void>();
    public readonly onDidChangeKeyboardMapper: Event<void> = this._onDidChangeKeyboardMapper.event;

    private _userKeyboardLayout: UserKeyboardLayout;

    private readonly layoutChangeListener = this._register(new MutableDisposable());
    private _factory: BrowserKeyboardMapperFactory;

    constructor(
        @inject(ClientTypes.IEnvironmentService)
        public environmentService: IEnvironmentService,
        @inject(FilesTypes.IFileService)
        public fileService: IFileService,
        @inject(ClientTypes.ILifecycleService)
        private lifecycleService: ILifecycleService,
        @inject(ClientTypes.INotificationService)
        public notificationService: INotificationService,
        @inject(ClientTypes.IStorageService)
        public storageService: IStorageService,
        @inject(ClientTypes.ICommandService)
        public commandService: ICommandService,
        @inject(ClientTypes.IConfigurationService)
        private configurationService: IClientConfigurationService,
    ) {
        super();
    }

    @postConstruct()
    private postSetup() {
        this.lifecycleService.when(LifecyclePhase.Restored).then(() => {
            const keyboardConfig = this.configurationService.getValue<{ layout: string }>('keyboard');
            const layout = keyboardConfig && keyboardConfig.layout;
            this._factory = new BrowserKeyboardMapperFactory(this.notificationService, this.storageService, this.commandService);

            this.registerKeyboardListener();

            if (layout && layout !== 'autodetect') {
                // set keyboard layout
                this._factory.setKeyboardLayout(layout);
            }

            this._register(this.configurationService.onDidChangeConfiguration(e => {
                if (e.affectedKeys.indexOf('keyboard.layout') >= 0) {
                    const tempKeyboardConfig = this.configurationService.getValue<{ layout: string }>('keyboard');
                    const tempLayout = tempKeyboardConfig.layout;

                    if (tempLayout === 'autodetect') {
                        this.registerKeyboardListener();
                        this._factory.onKeyboardLayoutChanged();
                    } else {
                        this._factory.setKeyboardLayout(tempLayout);
                        this.layoutChangeListener.clear();
                    }
                }
            }));

            this._userKeyboardLayout = new UserKeyboardLayout(this.environmentService.keyboardLayoutResource, this.fileService);
            this._userKeyboardLayout.initialize().then(() => {
                if (this._userKeyboardLayout.keyboardLayout) {
                    this._factory.registerKeyboardLayout(this._userKeyboardLayout.keyboardLayout);

                    this.setUserKeyboardLayoutIfMatched();
                }
            });

            this._register(this._userKeyboardLayout.onDidChange(() => {
                let userKeyboardLayouts = this._factory.keymapInfos.filter(layoutData => layoutData.isUserKeyboardLayout);

                if (userKeyboardLayouts.length) {
                    if (this._userKeyboardLayout.keyboardLayout) {
                        userKeyboardLayouts[0].update(this._userKeyboardLayout.keyboardLayout);
                    } else {
                        this._factory.removeKeyboardLayout(userKeyboardLayouts[0]);
                    }
                } else {
                    if (this._userKeyboardLayout.keyboardLayout) {
                        this._factory.registerKeyboardLayout(this._userKeyboardLayout.keyboardLayout);
                    }
                }

                this.setUserKeyboardLayoutIfMatched();
            }));
        });
    }

    public setUserKeyboardLayoutIfMatched() {
        const keyboardConfig = this.configurationService.getValue<{ layout: string }>('keyboard');
        const layout = keyboardConfig.layout;

        if (layout && this._userKeyboardLayout.keyboardLayout) {
            if (getKeyboardLayoutId(this._userKeyboardLayout.keyboardLayout.layout) === layout && this._factory.activeKeymap) {

                if (!this._userKeyboardLayout.keyboardLayout.equal(this._factory.activeKeymap)) {
                    this._factory.setActiveKeymapInfo(this._userKeyboardLayout.keyboardLayout);
                }
            }
        }
    }

    public registerKeyboardListener() {
        this.layoutChangeListener.value = this._factory.onDidChangeKeyboardMapper(() => {
            this._onDidChangeKeyboardMapper.fire();
        });
    }

    public getKeyboardMapper(dispatchConfig: DispatchConfig): IKeyboardMapper {
        return this._factory.getKeyboardMapper(dispatchConfig);
    }

    public getCurrentKeyboardLayout(): IKeyboardLayoutInfo | null {
        return this._factory.activeKeyboardLayout;
    }

    public getAllKeyboardLayouts(): IKeyboardLayoutInfo[] {
        return this._factory.keyboardLayouts;
    }

    public getRawKeyboardMapping(): IKeyboardMapping | null {
        return this._factory.activeKeyMapping;
    }

    public validateCurrentKeyboardMapping(keyboardEvent: IKeyboardEvent): void {
        this._factory.validateCurrentKeyboardMapping(keyboardEvent);
    }
}
